import React, { useContext, useState } from 'react'
import moment from 'moment'
import { Alert, Button, Stack, Typography } from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import DocumentsField from '../../../../helpers/DocumentsField'
import {
  DEMO_UPLOAD_MESSAGE,
  uploadDocument,
} from '../../../../helpers/uploadDocument'
import { SectionHeader } from '../../vehicles/VehicleComponents/SectionKit'
import { AssetContext } from './SingleAsset'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Photos, manuals and certificates kept against the asset. Changes are staged
// (removals can be undone) and written in one save, uploading through the
// shared presigned-URL helper so a failed PUT can't leave a dangling key.
const AttachmentsSection = () => {
  const { asset, setAsset } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [files, setFiles] = useState([])
  const [removed, setRemoved] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const existing = (asset.attachments ?? []).filter((a) => a?.key)
  const existingKeys = existing.map((a) => a.key)
  const dirty = files.length > 0 || removed.length > 0

  const toggleRemove = (key) =>
    setRemoved((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  const discard = () => {
    setFiles([])
    setRemoved([])
    setError(null)
  }

  const save = async () => {
    if (saving || !dirty) return
    setSaving(true)
    setError(null)
    try {
      // One at a time - presigned URLs only live for 60s
      const uploadedKeys = []
      for (const file of files) {
        uploadedKeys.push(await uploadDocument(file, 'fleetequipment/assets'))
      }
      const nextAttachments = [
        ...existing.filter((a) => !removed.includes(a.key)),
        ...uploadedKeys.map((key) => ({
          key,
          uploadedBy: user._id,
          createdAt: new Date(),
        })),
      ]
      const res = await API.put(`/asset/${asset._id}`, {
        attachments: nextAttachments,
      })
      if (res.status !== 200) throw new Error('Save failed')

      // Only once the record no longer references them
      await Promise.allSettled(
        removed.map((key) => API.post('/deletes3object', { key })),
      )

      setAsset((prev) => ({ ...prev, attachments: nextAttachments }))
      setFiles([])
      setRemoved([])
      setToast({ message: 'Attachments updated' })
    } catch (err) {
      console.error('Unable to save attachments', err)
      setError(
        err?.message === DEMO_UPLOAD_MESSAGE
          ? DEMO_UPLOAD_MESSAGE
          : files.length
            ? 'Unable to save - a file may have failed to upload. Check your connection and try again.'
            : 'Unable to save attachments. Please try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  const pending = [
    files.length > 0 && `${plural(files.length, 'file')} to upload`,
    removed.length > 0 && `${plural(removed.length, 'attachment')} to remove`,
  ].filter(Boolean)

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Attachments"
        caption="Photos, manuals, calibration certificates and other documents for this asset"
      />
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <DocumentsField
        existingDocuments={existingKeys}
        removedDocuments={removed}
        onToggleRemove={toggleRemove}
        files={files}
        onFilesChange={setFiles}
        disabled={saving}
        caption="Photos, manuals or certificates — PDF, Word or images"
        existingLabel={(key, index) => {
          const record = existing.find((a) => a.key === key)
          return record?.createdAt
            ? `Attachment ${index + 1} · added ${moment(record.createdAt).format('D MMM YYYY')}`
            : `Attachment ${index + 1}`
        }}
      />
      {dirty && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
          sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary">
            {saving && files.length ? 'Uploading…' : pending.join(' · ')}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button color="inherit" onClick={discard} disabled={saving}>
              Discard
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default AttachmentsSection
